import React, { useEffect, useRef, useState } from "react";

import TextField from "../../Components/Commons/Forms/TextField";
import BtnSave from "../../Components/Commons/Buttons/BtnSave";
import BtnEdit from "../Commons/Buttons/BtnEdit";
import BtnDelete from "../Commons/Buttons/BtnDelete";
import InputError from "../InputError/InputError";
import AntDTable from "../AntDTable/AntDTable";
import DateField from "../Commons/Forms/DateField";
import SelectField from "../Commons/Forms/SelectField";
import DeleteModal from "../Modals/DeleteModal";
import { SeeAttachment } from "../../Components/Radiology/SeeAttachment";
import { Upload, Button as AntDButton } from "antd";
import { Form, Modal, Row, Col, Table as RBSTable } from "react-bootstrap";
import {
  createMultipleLabs,
  deleteLabResult,
  getLabResults,
  getLabTests,
  updateLabResult,
} from "../Helpers/API/LabApi/LaboratoryApi";
import { isLabResultFormHasNull } from "../Helpers/Utilities/Validations/Laboratory";
import { toast } from "react-toastify";
import {
  formatDate,
  formatDateYMD,
  getDateToday,
  getRole,
  getUser,
  validateFileSize,
  compressPdf,
} from "../Helpers/Utilities/Common";
import { getAllDoctors } from "../Helpers/API/DoctorApi/DoctorApi";
import { UploadOutlined } from "@ant-design/icons";
import imageCompression from "browser-image-compression";
import { getLabCategoriesQR } from "../Helpers/API/QrDiagnosticsApi/QrDiagnosticsApi";
import { examData } from "../Helpers/Constants/ExamData";
import { convertImageFromUrlToFileVM5 } from "../../Utils/Common";
function LabResultPage(props) {
  const role = getRole();
  const userId = getUser();
  const [addDetails, setAddDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [laboratories, setLaboratories] = useState([]);
  const [selectedLabTest, setSelectedLabTest] = useState("");
  const [selectedLabTestName, setSelectedLabTestName] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedPerformedBy, setSelectedPerformedBy] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const labResultFormRef = useRef(null);

  async function handleAddChange(e, laboratory, index, isFile, selectedOption = null) {
    if (isFile) {
      const attachment = e.target.files[0];
      if (attachment) {
        let fileToUpload = attachment;
        const fileSizeMB = attachment.size / 1024 / 1024;
        
        // Compress images if they exceed 5MB
        if (fileToUpload.type.startsWith("image/") && fileSizeMB > 5) {
          const options = {
            maxSizeMB: 5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(fileToUpload, options);

          // Convert blob back to File
          fileToUpload = new File([compressedBlob], attachment.name, {
            type: attachment.type,
            lastModified: Date.now(),
          });
        }
        
        // Compress PDFs to 2MB
        if (fileToUpload.type === "application/pdf" && fileSizeMB > 2) {
          fileToUpload = await compressPdf(fileToUpload, 2);
        }

        // Update state with the (possibly compressed) file
        setAddDetails((prevState) => {
          const newState = [...prevState];
          newState[index] = {
            ...newState[index],
            attachments: fileToUpload,
            lab_id: laboratories[selectedLabTest]?.id,
            lab_test_name: laboratory.test,
          };
          return newState;
        });
      } else {
        setAddDetails((prevState) => {
          const newState = [...prevState];
          newState[index] = {
            ...newState[index],
            attachments: "",
          };
          return newState;
        });
      }
    } else {
      const { name, value } = e.target;
      if (value || selectedOption) {
        setAddDetails(() => {
          const newState = [...addDetails];
          newState[index] = {
            ...newState[index],
            [name]: value,
            lab_id: laboratories[selectedLabTest]?.id,
            lab_test_name: laboratory.test,
            option_id: selectedOption // check this first, this is only applicable for dropdown fields
          };
          return newState;
        });
      } else {
        setAddDetails((prevState) => {
          const newState = [...prevState];
          newState[index] = {
            ...newState[index],
            [name]: "",
          };
          return newState;
        });
      }
    }
  }

  const handleAddDoctorChange = (e) => {
    setSelectedDoctor(e.id);
  };

  const findBreakdownMatch = (breakdown, { key, test }) => {
    return breakdown.find((item) => {
      const savedValue = (item.analyte || item.key || "").toLowerCase();
      const labKey = (key || "").toLowerCase();

      if (labKey && savedValue) {
        return savedValue === labKey;
      }

      const savedName = (item.analyte || item.lab_test_name || "").toUpperCase();
      return savedName === (test || "").toUpperCase();
    });
  };

  const buildLabTestsFromExam = (labName, fallback = {}, breakdown = []) => {
    const matchedExam = examData.find(
      (exam) => exam.cisExamName.toUpperCase() === (labName || "").toUpperCase()
    );
    if (matchedExam) {
      return matchedExam.items.map((item) => {
        const match = findBreakdownMatch(breakdown, {
          key: item.key,
          test: item.name,
        });
        return {
          lab_test_breakdown_id: match?.id || "",
          test: item.name,
          unit: item.unit,
          value: match?.id || item.key,
          label: item.name,
          key: item.key,
          type: item.type,
          options: item.options,
          maxLength: item.maxLength,
          showIf: item.showIf,
        };
      });
    }

    const match = findBreakdownMatch(breakdown, { key: "", test: fallback.test });
    return [
      {
        lab_test_breakdown_id: match?.id || "",
        test: fallback.test,
        unit: "",
        value: fallback.id,
        label: fallback.test,
        key: fallback.test, // this will serve as the analyte for non yakap tests
        result: null,
      },
    ];
  };

  const handleLabChange = (e) => {
    const lab_tests = buildLabTestsFromExam(e?.lab_name, {
      id: e?.id,
      test: e?.lab_name,
    });

    setSelectedLabTest(e?.lab_index);
    setSelectedLabTestName(e?.lab_name);
    setAddDetails(
      lab_tests.map((labTest, index) => ({
        lab_id: e?.id,
        lab_test_name: labTest.test,
        result: "",
        ...(index === 0 ? { attachments: "" } : {}),
      }))
    );

    setLaboratories((prev) =>
      prev.map((lab) =>
        lab.lab_index === e?.lab_index ? { ...lab, lab_tests } : lab
      )
    );
  };

  const handleViewResult = (rowData) => {
    const labName = rowData.lab_name || rowData.lab_test_name;
    const labIndex = laboratories.findIndex(
      (lab) => lab.lab_name.toUpperCase() === labName.toUpperCase()
    );
    if (labIndex === -1) {
      toast.error("Lab test not found in current category.");
      return;
    }

    const selectedLab = laboratories[labIndex];
    const breakdown = rowData.breakdowns || [];
    const lab_tests = buildLabTestsFromExam(
      labName,
      { id: selectedLab.id, test: labName },
      breakdown
    );

    setLaboratories((prev) =>
      prev.map((lab, i) => (i === labIndex ? { ...lab, lab_tests } : lab))
    );

    const autoFillData = lab_tests.map((labTest, index) => {
      const match = findBreakdownMatch(breakdown, {
        key: labTest.key,
        test: labTest.test,
      });
      return {
        lab_test_breakdown_id: labTest.lab_test_breakdown_id || "",
        lab_id: selectedLab.id,
        lab_test_name: labTest.test,
        result: match?.result || "",
        ...(index === 0 ? { attachments: rowData.attachments || "" } : {}),
      };
    });

    setAddDetails(autoFillData);
    setSelectedLabTest(labIndex);
    setSelectedLabTestName(labName);
    setSelectedDate(
      rowData.result_date ? formatDateYMD(rowData.result_date) : ""
    );
    setSelectedFacility(rowData.reference_facility || rowData.facility || "");
    setSelectedPerformedBy(rowData.performed_by || "");
    setSelectedDoctor(rowData.doctor_id ?? null);

    setSelectedRow((prevState) => ({
      ...prevState,
      lab_result_id: rowData.id || "",
    }));

    requestAnimationFrame(() => {
      labResultFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedRow((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditDoctorChange = (e) => {
    setSelectedRow((prevState) => ({
      ...prevState,
      formattedDoctor: {
        value: e.value, //doctor id
        label: e.label, //doctor name
      },
    }));
    setSelectedDoctor(e.id);
  };

  const handleBeforeEditUpload = async (file) => {
    let compressedFile = file;
    const fileSizeMB = file.size / 1024 / 1024;

    // Compress images if they exceed 5MB
    if (file.type.startsWith("image/") && fileSizeMB > 5) {
      const options = {
        maxSizeMB: 5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      const compressedBlob = await imageCompression(file, options);
      // Convert the blob into a File and preserve the original name/type
      compressedFile = new File([compressedBlob], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });
    }
    
    // Compress PDFs to 2MB
    if (file.type === "application/pdf" && fileSizeMB > 2) {
      compressedFile = await compressPdf(file, 2);
    }

    setSelectedRow((prevState) => ({
      ...prevState,
      attachments: compressedFile,
    }));
    return false; // Prevent auto upload
  };

  function ActionBtn(row) {
    return (
      <Row className="center">
        <Col>
          <BtnSave
            label={"View Result"}
            size={"sm"}
            onClick={() => handleViewResult(row.row)}
          />
        </Col>
        {/* {(role === "specialist" || role === "pcp" || role === "director") && (
          <Col>
            <BtnDelete
              outline
              label={"DELETE"}
              size={"sm"}
              onClick={(e) => {
                setShowDelete(true);
                setSelectedRow(row.row);
              }}
            />
          </Col>
        )} */}
      </Row>
    );
  }

  const handleSave = async () => {
  setIsLoading(true);

  const selectedLab = laboratories[selectedLabTest];
  const examAttachment = addDetails[0]?.attachments || ""; // ← attachment gikan sa exam row

  const filteredDetails = laboratories[selectedLabTest].lab_tests
    .map((labTest, index) => ({
      lab_test_breakdown_id: labTest.lab_test_breakdown_id || "",
      lab_id: selectedLab?.id || "",
      lab_test_name: labTest.test,
      result: addDetails[index]?.result || "",
      facility: selectedFacility || "",
      performed_by: selectedPerformedBy || "",
      key: labTest.key || "",
      option_id: addDetails[index]?.option_id || "",
    }))
    .filter((item) => item?.result && item.result.trim() !== "") || [];

  const res = await createMultipleLabs({
    patient_id: props.id,
    filteredDetails,
    types: props.categoryData.tab_label,
    selectedDate: selectedDate,
    doctor_id: !isPHICYakapInclusion() ? selectedDoctor : null,
    lab_test_id: selectedLab?.id || "",
    lab_result_id: selectedRow.lab_result_id || "",
    lab_test_name: selectedLab?.lab_name || "",
    attachments: examAttachment, // ← i-pass diri
    facility: selectedFacility || "",
    performed_by: selectedPerformedBy || "",
    is_phic_yakap: isPHICYakapInclusion(),
  });

  if (res.data) {
    toast.success(res.data.message);
    props.onLabSaved();
    setAddDetails([]);
    setSelectedLabTest("");
    setSelectedLabTestName("");
    setSelectedDate("");
    setSelectedFacility("");
    setSelectedPerformedBy("");
    setSelectedDoctor(role === "nurse" || role === "laboratory" ? null : userId);
    setSelectedRow({});
  } else if (res.error) {
    const messages = res?.error?.data?.messages;
    if (Array.isArray(messages)) {
      messages.forEach((err) => toast.error(err));
    } else if (messages?.error_messages && Array.isArray(messages.error_messages)) {
      messages.error_messages.forEach((err) => toast.error(err));
    } else if (typeof messages === "string") {
      toast.error(messages);
    } else {
      toast.error("An error occurred. Please try again.");
    }
  }
  setIsLoading(false);
};

  const handleEdit = async () => {
    setIsLoading(true);
    const res = await updateLabResult(selectedRow, selectedDoctor);
    if (res.data) {
      toast.success(res.data.message);
      setSelectedRow({});
      setShowEdit(false);
      fetchLabResults();
    } else if (res.error) {
      res.error.data.messages.forEach((err) => {
        toast.error(err);
      });
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    const res = await deleteLabResult(selectedRow.id);
    if (res.data) {
      toast.success(res.data.message);
      setSelectedRow({});
      setShowDelete(false);
      fetchLabResults();
    } else if (res.error) {
      res.error.data.messages.forEach((err) => {
        toast.error(err);
      });
    }
    setIsLoading(false);
  };

  // Function for get all doctors API
  async function fetchAllDoctors() {
    const response = await getAllDoctors();
    if (response.data) {
      var data = response.data.data.map((data, index) => {
        var info = data;
        info.label = data.doctor_name;
        info.value = data.id;
        return data;
      });
      setDoctors(data);
    } else {
      setDoctors([]);
    }
  }

async function fetchLaboratoryTests() {
  const response = await getLabCategoriesQR({
    category_id: props?.categoryData?.id,
  });
  const key = props?.categoryData?.tab_label;

  if (response.error) {
    setLaboratories([]);
  } else if (response.data?.[key]) {
    const data = response.data[key].map((lab, index) => {
      return {
        id: lab.id,
        lab_name: lab.lab_name,
        lab_index: index,
        label: lab.lab_name,
        value: lab.lab_name.replace(/ /g, "_").toLowerCase(),
        lab_tests: [
          {
            id: lab.id,
            test: lab.lab_name,
            value: lab.id,
            label: lab.lab_name,
          },
        ],
      };
    });
    setLaboratories(data);
  } else {
    setLaboratories([]);
  }
}

  async function fetchLabResults() {
    setShowLoader(true);
    const res = await getLabResults({
      lab_test_type: props.categoryData.tab_label,
      lab_test_sub_type: "",
      patient_id: props.id,
    });
    if (res.data) {
      var _parsed = await Promise.all(
        res.data.data.map(async (laboratory) => {
          const attachments = await convertImageFromUrlToFileVM5(laboratory?.attachments);
          return {
            ...laboratory,
            formattedResultDate: formatDate(laboratory.result_date),
            lab_result_attachments: (
              <SeeAttachment attachments={attachments} />
            ),
            attachments: attachments,
            formattedDoctor: {
              value: laboratory.doctor_id,
              label: laboratory.doctor_name,
            },
            // reference_facility: "Test Facility WIWI", // dummy
            // performed_by: "Test Performed By WIWI", // dummy
            lab_result_id: laboratory.id,
            // lab_result_breakdown: [
            //   {
            //     id: "11",
            //     lab_result_id: "4",
            //     analyte: "hematocrit",
            //     result: "half" // dummy
            //   },
            //   {
            //     id: "12",
            //     lab_result_id: "4",
            //     analyte: "hemoglobin_g",
            //     result: "20"
            //   },
            //   {
            //     id: "33",
            //     lab_result_id: "4",
            //     analyte: "hemoglobin_mmol",
            //     result: "10"
            //   },
            //   {
            //     id: "44",
            //     lab_result_id: "4",
            //     analyte: "neutrophils_seg",
            //     result: "50"
            //   },
            // ]
          };
        })
      );
      setData(_parsed);
    } else if (res.error) {
      setData([]);
    }
    setShowLoader(false);
  }

  useEffect(() => {
    fetchAllDoctors();
    fetchLaboratoryTests();
    fetchLabResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (role === "nurse" || role === "laboratory") {
      setSelectedDoctor(null);
    } else {
      setSelectedDoctor(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);
  

  const isPHICYakapInclusion = () => {
    // service name should be one of the following
    // ✅ - included
    // ‼️ - not included
    // ⚠️ - in documentation but not in the list below
    const serviceNames = [
      "ECG (12 LEADS)", // ✅
      "CHEST X-RAY PA VIEW", // ✅
      "BREAST ULTRASOUND", // ‼️
      "UPPER ABDOMEN ULTRASOUND", // ‼️
      "PELVIC ULTRASOUND - SINGLETON WITH CERVICAL LENGTH", // ‼️
      "PELVIC ULTRASOUND - TWINS WITH CERVICAL LENGTH", // ‼️
      "CREATININE, SERUM", // ✅
      "FASTING BLOOD SUGAR (FBS)", //✅
      "GLUCOSE (AFTER 75G GLUCOSE LOAD) - 75GOGTT", // ✅
      "HBA1C", // ✅
      "LIPID PROFILE", // ✅
      "FECAL OCCULT BLOOD (RAPID)", // ✅
      "FECALYSIS", // ✅
      "URINALYSIS", // ✅
      "PAP SMEAR (CYTOPATH STUDY) CERVICOVAGINAL - (PCP)", // ✅
      "PPDTest", // ✅
      "COMPLETE BLOOD COUNT (CBC)", // ✅
      "(SPUTUM AFB)", // ✅

      "RANDOM BLOOD SUGAR (RBS)", // ⚠️

      // PPDTEST // ‼️
    ];
    
    return serviceNames.includes(selectedLabTestName);
  }

  return (
    <div className="pb-4">
      <div className="row" ref={labResultFormRef}>
        <label className="col-6 font-bold mt-2">
          {props?.categoryData?.name} RESULTS FORM
        </label>
        <p>
          If you cannot find {props?.categoryData?.tab_label} lab test, save
          result in Others section.
        </p>
        <div className="col-sm-3" style={{zIndex: "11" }}>
          <DateField
            required
            formLabel={`DATE`}
            name="date"
            size={`md`}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={getDateToday()}
          />
        </div>
        <div className="col-sm-3" style={{ zIndex: "11" }}>
          <SelectField
            required
            searchable
            formLabel={`${props.categoryData?.name} TESTS`}
            name="laboratory"
            size={`sm`}
            placeholder={"SELECT"}
            className="dropsearch-filter"
            options={laboratories}
            value={laboratories[selectedLabTest] || ""}
            onChange={(e) => handleLabChange(e)}
          />
        </div>
        {isPHICYakapInclusion() && 
          <div className="col-sm-3" style={{zIndex: "11" }}>
            <TextField
              required
              formLabel={`Facility`}
              name="facility"
              size={`md`}
              onChange={(e) => setSelectedFacility(e.target.value)}
              value={selectedFacility}
            />
          </div>
        }
        {isPHICYakapInclusion() && 
          <div className="col-sm-3" style={{zIndex: "11" }}>
            <TextField
              required
              formLabel={`Performed By`}
              name="performed_by"
              size={`md`}
              onChange={(e) => setSelectedPerformedBy(e.target.value)}
              value={selectedPerformedBy}
            />
          </div>
        }
        {/* {((role === "nurse" || role === "laboratory") && !isPHICYakapInclusion()) && (
          <>
            <div className="col-sm-3" style={{ zIndex: "11" }}>
              <SelectField
                // required
                searchable
                formLabel={`ATTENDING PHYSICIAN`}
                name="doctor_id"
                size={`sm`}
                placeholder={"SELECT"}
                className="dropsearch-filter"
                options={doctors}
                value={
                  doctors.find((doctor) => doctor.value === selectedDoctor) ||
                  ""
                }
                onChange={(e) => handleAddDoctorChange(e)}
              />
            </div>
          </>
        )} */}
      </div>
      <div className="row">
        {selectedLabTest !== "" && (
          <RBSTable bordered className="lab-table">
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Lab test</th>
                <th style={{ width: "30%" }}>Result</th>
                <th style={{ width: "30%" }}>File</th>
              </tr>
            </thead>
            <tbody>
              {/* Row 1 — Exam name + File upload */}
              <tr>
                <td style={{ width: "30%" }} className="m-0 font-bold">
                  {laboratories[selectedLabTest]?.lab_name}
                </td>
                <td style={{ width: "30%" }} className="m-0"></td>
                <td style={{ width: "30%" }} className="m-0">
                  <input
                    key={selectedLabTest}
                    type="file"
                    accept=".pdf,image/*"
                    className="form-control date-filter gotham"
                    onChange={(e) => {
                      const attachment = e.target.files[0];
                      setAddDetails((prevState) => {
                        const newState = [...prevState];
                        newState[0] = {
                          ...newState[0],
                          attachments: attachment || "",
                        };
                        return newState;
                      });
                    }}
                    disabled={isLoading}
                  />
                </td>
              </tr>

              {/* Analyte rows — result only, no file */}
              {laboratories[selectedLabTest].lab_tests.map((data, index) => {
              // Skip rendering kung naay showIf condition nga wala natugma
              if (data.showIf) {
                const depIndex = laboratories[selectedLabTest].lab_tests.findIndex(
                  (lt) => lt.key === data.showIf.key
                );
                const depValue = addDetails[depIndex]?.result;

                if (depValue !== data.showIf.value) {
                  return null;
                }
              }

              return (
                <tr key={index}>
                  <td className="m-0">{data.test} {data.unit && data.unit !== "" ? `(${data.unit})` : ""}</td>
                  <td className="m-0">
                      {data.type === "select" ? (
                        <SelectField
                          name="result"
                          value={addDetails[index]?.result}
                          size="sm"
                          showPlaceholder
                          placeholder="SELECT"
                          onChange={(e) => {
                            const value = e.target.value;
                            const selectedOption = data.options.find(option => option.label === value)?.key;
                            handleAddChange(e, data, index, false, selectedOption)
                          }}
                          options={data.options}
                        /> 
                      ) : data.type === "number" ? (
                        <TextField
                          name="result"
                          value={addDetails[index]?.result}
                          size="sm"
                          onKeyDown={(e) => {
                            const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
                            const isDigit = /^[0-9]$/.test(e.key);
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const cleanValue = e.target.value.replace(/[^0-9]/g, "");
                            const fakeEvent = {
                              ...e,
                              target: { ...e.target, name: e.target.name, value: cleanValue },
                            };
                            handleAddChange(fakeEvent, data, index, false);
                          }}
                        />
                      ) : (
                        <TextField
                          name="result"
                          value={addDetails[index]?.result}
                          size="sm"
                          maxLength={data.maxLength}
                          onChange={(e) => handleAddChange(e, data, index, false)}
                          onBlur={(e) => {
                            if (data.maxLength && e.target.value.length >= data.maxLength) {
                              toast.warning(`Maximum of ${data.maxLength} characters only.`);
                            }
                          }}
                        />
                      )}
                  </td>
                  <td className="m-0"></td>
                </tr>
              );
            })}
            </tbody>
          </RBSTable>
        )}
      </div>
      {selectedLabTest !== "" && (
        <div className="row justify-content-end mt-3 mb-3">
          <div className="col-2">
            <BtnSave
              label={"Submit"}
              size={"sm"}
              onClick={handleSave}
              isClicked={isLoading}
              disabled={
                !selectedDate ||
                // (!isPHICYakapInclusion() ? !selectedDoctor : false) ||
                (isPHICYakapInclusion() && (!selectedPerformedBy || !selectedFacility)) ||
                isLabResultFormHasNull(addDetails)
              }
            />
          </div>
        </div>
      )}
      {/* Edit Lab Result */}
      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        backdrop="static"
        keyboard={false}
        size="md"
        centered
      >
        <Modal.Body>
          <div className="row">
            <Form.Label className="form-title align-center">
              Edit Lab Result
            </Form.Label>

            <div className="mt-2 mb-2 col-12 align-left">
              {selectedRow.lab_name === selectedRow.lab_test_name
                ? `Laboratory: ${selectedRow.lab_test_name}`
                : `Laboratory: ${selectedRow.lab_name} - ${selectedRow.lab_test_name}`}
            </div>
          </div>
          <Row>
            <DateField
              required
              className="mt-0"
              id="result_date"
              name="result_date"
              formLabel={`Result Date`}
              size={`sm`}
              onChange={(e) => handleEditChange(e, false)}
              value={selectedRow.result_date}
              errorMessage="Date is required."
              max={getDateToday()}
            />
            <TextField
              // required
              id="result"
              name="result"
              formLabel={`Result`}
              size={`sm`}
              onChange={handleEditChange}
              value={selectedRow.result}
              // errorMessage="Result is required."
            />
          </Row>
          {(role === "nurse" || role === "laboratory") && (
            <>
              <div className="row" style={{ zIndex: "11" }}>
                <SelectField
                  required
                  searchable
                  formLabel={`Attending Physician`}
                  name="doctor_id"
                  size={`sm`}
                  placeholder={"SELECT"}
                  className="dropsearch-filter"
                  value={selectedRow.formattedDoctor}
                  options={doctors}
                  onChange={(e) => handleEditDoctorChange(e)}
                />
              </div>
            </>
          )}
          <Row>
            <Form.Label className="form-label align-left">
              Attachment
            </Form.Label>
            <Upload
              accept=".pdf, image/*"
              maxCount={1}
              defaultFileList={
                selectedRow?.attachments
                  ? [
                      {
                        url: selectedRow?.attachments?.file_url || "",
                        name: selectedRow?.attachments?.file_name || "",
                        status: "done",
                      },
                    ]
                  : []
              }
              beforeUpload={(file) => handleBeforeEditUpload(file)}
              onRemove={(file) => {
                setSelectedRow((prevState) => ({
                  ...prevState,
                  attachments: null,
                }));
              }}
            >
              <AntDButton icon={<UploadOutlined />}>Select File</AntDButton>
              {/* <InputError
                isValid={!selectedRow.attachments}
                message={"Attachment is required."}
              /> */}
            </Upload>
          </Row>
          <Row className="mt-4">
            <Col>
              <BtnDelete
                label="Close"
                onClick={() => setShowEdit(false)}
                isClicked={isLoading}
              />
            </Col>
            <Col>
              <BtnEdit
                disabled={
                  // !selectedRow.result ||
                  !selectedRow.result_date ||
                  (!selectedRow.attachments && !selectedRow.result)
                }
                label="Save changes"
                onClick={handleEdit}
                isClicked={isLoading}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onDelete={handleDelete}
        title={`Are you sure you want to delete this laboratory result?`}
      />
    </div>
  );
}

export default LabResultPage;
